// CONTRACT:C1-MAP-CLIENT.1.0
// COP — OPERATOR ALERT-ACK STATE (read / cleared), extracted pure from cop.ts so the
// hydrate / persist / re-fire-unread / GC-prune semantics are headlessly testable.
//
// This is operator SELF-SCOPE state (a UI acknowledgement / dismiss), NOT a security
// decision: clearing an alert removes it from THIS operator's log (and quiets the
// VerdictGlyph, which counts only un-dismissed alerts) but never from the registry/map —
// a re-emit re-adds it (it re-appears UNread, which is correct: a re-fired alert
// deserves attention). The caller (cop.ts) guards every mutation to ids already in the
// cleared-VISIBLE alert log, so state is never fabricated for a hidden id (Glomar).
//
// PERSISTENCE — per-user via the injected AlertAckPersistence (cop.ts wires it to the
// CONTRACT:I8-LOCAL-SETTINGS per-persona store: leaves alerts.readIds/alerts.clearedIds,
// identity-scoped so a no-persona write is dropped by the store, never device-bled).
// Every mutation that changes the sets persists; hydrate() (boot + persona re-scope)
// only loads.
//
// GC / RE-FIRE SEMANTICS — gc(liveIds) prunes ack state for ids no longer visible, so a
// future re-emit of the SAME id re-surfaces it unread. Two guards keep persistence honest:
//   • SETTLE GRACE: for settleGraceMs after a hydrate, an id that has NEVER been seen
//     live this session is exempt from pruning — the boot feed arrives incrementally, and
//     a persisted ack must not be GC'd before its alert has had a chance to arrive.
//   • SEEN-THEN-GONE: an id observed live in a prior gc pass and now absent prunes
//     immediately (even inside the grace) — a real departure keeps the re-fire-unread
//     contract on the normal cadence.
// Hydrated ids whose alerts never return are pruned once the grace expires ("inert
// display-state, pruned in time"); the persisted arrays are additionally capped.

/** The persisted shape: the two ack id arrays (insertion-ordered, oldest first). */
export interface AlertAckSnapshot {
  readIds: string[];
  clearedIds: string[];
}

/** Where ack state lives across reloads. cop.ts binds this to the I8 per-persona settings
 *  store; tests inject a fake. save() receives already-capped arrays. */
export interface AlertAckPersistence {
  /** Load the ACTIVE identity's persisted ack arrays (empty arrays when none). */
  load(): AlertAckSnapshot;
  /** Persist the ack arrays for the ACTIVE identity (the store drops the write when no
   *  persona is active — identity-scoped, never device-bled). */
  save(snap: AlertAckSnapshot): void;
}

export interface AlertAcksOptions {
  persistence: AlertAckPersistence;
  /** Cap on each PERSISTED array (most recent kept). Default 100 — aligned with the
   *  host's ALERT_LOG_CAP and the I8 validator's ALERT_ACK_CAP. */
  cap?: number;
  /** Clock seam (tests). Default Date.now. */
  now?: () => number;
  /** The settle grace window after a hydrate (see header). Default 30 s. */
  settleGraceMs?: number;
}

export interface AlertAcks {
  /** Has the operator marked this alert read? */
  isRead(id: string): boolean;
  /** Has the operator dismissed this alert from their log? */
  isCleared(id: string): boolean;
  /** Mark ONE alert read. Returns false (and does not persist) if already read. */
  markRead(id: string): boolean;
  /** Dismiss ONE alert (the per-row ×). Drops its read mark too (a later re-fire starts
   *  clean). Returns false (no persist) if already dismissed. */
  dismiss(id: string): boolean;
  /** Dismiss EVERY given id (clear-all over the visible log) and wipe all read marks
   *  (the log is empty afterwards — nothing left to be "read"). Returns whether anything
   *  changed. */
  clearAll(ids: readonly string[]): boolean;
  /** Prune ack state for ids not in `liveIds` (grace-guarded — see header). Persists iff
   *  something was pruned; returns whether it was. Call on the live-log cadence. */
  gc(liveIds: ReadonlySet<string>): boolean;
  /** (Re)load from persistence, replacing the sets; resets the settle grace + seen set.
   *  Boot + persona re-scope. Never writes. */
  hydrate(): void;
  /** Wipe the sets AND the persisted arrays (theater switch: the old theater's ids must
   *  not bleed into the new one). */
  wipe(): void;
  /** The current (uncapped) in-memory state — test/inspection seam. */
  snapshot(): AlertAckSnapshot;
}

export function createAlertAcks(opts: AlertAcksOptions): AlertAcks {
  const { persistence } = opts;
  const cap = opts.cap ?? 100;
  const now = opts.now ?? Date.now;
  const settleGraceMs = opts.settleGraceMs ?? 30_000;

  const read = new Set<string>();
  const cleared = new Set<string>();
  /** Ids observed live in a PRIOR gc pass (the seen-then-gone detector). An id pruned at
   *  its first absent pass never needs longer memory, so "previous pass" suffices. */
  let prevLive = new Set<string>();
  let hydratedAt = now();

  /** Persist the capped snapshot (most recent kept — Sets iterate in insertion order). */
  const persist = (): void => {
    persistence.save({
      readIds: [...read].slice(-cap),
      clearedIds: [...cleared].slice(-cap),
    });
  };

  const hydrate = (): void => {
    const snap = persistence.load();
    read.clear();
    cleared.clear();
    for (const id of snap.readIds.slice(-cap)) read.add(id);
    for (const id of snap.clearedIds.slice(-cap)) cleared.add(id);
    prevLive = new Set();
    hydratedAt = now();
  };
  hydrate(); // construction = the boot hydrate (the store is already persona-scoped)

  return {
    isRead: (id) => read.has(id),
    isCleared: (id) => cleared.has(id),

    markRead(id): boolean {
      if (read.has(id)) return false;
      read.add(id);
      persist();
      return true;
    },

    dismiss(id): boolean {
      if (cleared.has(id)) return false;
      cleared.add(id);
      read.delete(id);
      persist();
      return true;
    },

    clearAll(ids): boolean {
      let changed = read.size > 0;
      read.clear();
      for (const id of ids) {
        if (!cleared.has(id)) {
          cleared.add(id);
          changed = true;
        }
      }
      if (changed) persist();
      return changed;
    },

    gc(liveIds): boolean {
      const settled = now() - hydratedAt >= settleGraceMs;
      const prunable = (id: string): boolean =>
        !liveIds.has(id) && (settled || prevLive.has(id));
      let changed = false;
      for (const id of [...cleared]) {
        if (prunable(id)) {
          cleared.delete(id);
          changed = true;
        }
      }
      for (const id of [...read]) {
        if (prunable(id)) {
          read.delete(id);
          changed = true;
        }
      }
      prevLive = new Set(liveIds);
      if (changed) persist();
      return changed;
    },

    hydrate,

    wipe(): void {
      read.clear();
      cleared.clear();
      prevLive = new Set();
      persist();
    },

    snapshot: () => ({ readIds: [...read], clearedIds: [...cleared] }),
  };
}
